import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dog, Camera, Heart, Trophy, PawPrint, ClipboardEdit, Info, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../utils/supabase';
import { PetRegistrationModal } from './PetRegistrationModal';
import { PetCard, type Pet } from './PetCard';
import { getVoterKey, loadVotedIds, persistVotedIds } from '../utils/petWall';
// Home preview shows only the top N pets; the full wall lives at /muro.
const HOME_LIMIT = 12;
export function PetWallSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const topCtaRef = useRef<HTMLDivElement | null>(null);
  const [isSectionInView, setIsSectionInView] = useState(false);
  const [isTopCtaInView, setIsTopCtaInView] = useState(false);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsSectionInView(entry.isIntersecting);
    }, {
      threshold: 0.05
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const node = topCtaRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsTopCtaInView(entry.isIntersecting);
    }, {
      threshold: 0.4
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // Sticky bottom CTA only shows on mobile when section is visible but the
  // top inline CTA is already out of view — avoids dual-button conflict.
  const showStickyBottomCta = isSectionInView && !isTopCtaInView;
  // Live pet wall — fed from the `public_pet_wall` view (only paid pets w/ photo).
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [votedIds, setVotedIds] = useState<Set<string>>(() => loadVotedIds());
  const fetchPets = async () => {
    const {
      data,
      error
    } = await supabase.from('public_pet_wall').select('id, name, breed, bio, photo_url, huellas').order('huellas', {
      ascending: false
    });
    if (!error && data) {
      setPets(data.map(row => ({
        id: row.id,
        name: row.name,
        breed: row.breed,
        bio: row.bio,
        imageUrl: row.photo_url,
        huellas: row.huellas ?? 0
      })));
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchPets();
    // Realtime — refresh huellas / new pets without reload
    const channel = supabase.channel('pet_wall_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'pet_huellas'
    }, () => fetchPets()).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'registration_pets'
    }, () => fetchPets()).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
  const handleDarHuella = async (id: string) => {
    if (votedIds.has(id)) {
      toast.info('Ya le diste tu huella a esta mascota 🐾');
      return;
    }
    // Optimistic update
    setPets(prev => prev.map(pet => pet.id === id ? {
      ...pet,
      huellas: pet.huellas + 1
    } : pet));
    const nextVoted = new Set(votedIds).add(id);
    setVotedIds(nextVoted);
    persistVotedIds(nextVoted);
    const {
      error
    } = await supabase.from('pet_huellas').insert({
      pet_id: id,
      voter_key: getVoterKey()
    });
    if (error && error.code !== '23505') {
      // Real failure — roll back the optimistic +1
      setPets(prev => prev.map(pet => pet.id === id ? {
        ...pet,
        huellas: Math.max(0, pet.huellas - 1)
      } : pet));
      const reverted = new Set(nextVoted);
      reverted.delete(id);
      setVotedIds(reverted);
      persistVotedIds(reverted);
      toast.error('No pudimos registrar tu huella. Intenta de nuevo.');
    }
  };
  const topPets = [...pets].sort((a, b) => b.huellas - a.huellas).slice(0, 5);
  return <section ref={sectionRef} id="muro" className="py-24 relative overflow-hidden" data-id="element-879" data-mp="3gsge2fj2gin9">
      <PetRegistrationModal open={modalOpen} onClose={() => setModalOpen(false)} data-id="element-880" data-mp="3cs5yqc9dwfvq" />
      {/* Paw prints background image */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-881" data-mp="3qp2yx4be79pa">
        <img src="https://cdn.magicpatterns.com/uploads/mVfgFvxDgxJwQdHZBf1guJ/PATRON_HUELLAS_fondo.png" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-882" data-mp="3lt434vnqipeu" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-883" data-mp="1rg5yosopumzf">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-884" data-mp="6wiyn4kusnc4">
          <h2 className="text-4xl md:text-5xl text-brand-navy mb-6" data-id="element-885" data-mp="bvn53nge86yr">
            {['El', 'Muro', 'de', 'las', 'Huellas'].map((word, i) => <motion.span key={i} initial={{
            opacity: 0,
            y: 30,
            filter: 'blur(10px)'
          }} whileInView={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="inline-block shimmer-text mr-3" data-id="element-886" data-mp="3tb29iramdkdu" data-mpi={i}>
                {word}
              </motion.span>)}
          </h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg text-gray-600 mb-10" data-id="element-887" data-mp="1di3d3897169q">
            Sube la foto de tu mascota, acumula Huellas y compite por ser la{' '}
            <strong data-id="element-888" data-mp="1uvmiqu9rines">Mascota Influencer 2026</strong>.
          </motion.p>

          {/* 3 Steps */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex flex-row justify-center items-start gap-2 md:gap-10" data-id="element-889" data-mp="1px1gyqev3vo8">
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-890" data-mp="3rhmaw4uo7acd">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-navy/10 flex items-center justify-center text-brand-navy mb-2 md:mb-3" data-id="element-891" data-mp="984mhfxs9vq">
                <Dog className="w-6 h-6 md:w-8 md:h-8" data-id="element-892" data-mp="4dpoegmaqn6i" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-893" data-mp="9c99yfwz9wyc">
                1. Regístralo
              </span>
            </div>
            <div className="hidden md:block w-12 h-0.5 bg-gray-200 mt-7" data-id="element-894" data-mp="j7u7a0r3q91r"></div>
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-895" data-mp="nz2h2xwcfwfg">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-cyan/10 flex items-center justify-center text-brand-cyan mb-2 md:mb-3" data-id="element-896" data-mp="s1632ggn2jez">
                <Camera className="w-6 h-6 md:w-8 md:h-8" data-id="element-897" data-mp="x1ljxi04q91p" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-898" data-mp="11yk69edjxbpc">
                2. Súbelo
              </span>
            </div>
            <div className="hidden md:block w-12 h-0.5 bg-gray-200 mt-7" data-id="element-899" data-mp="7xdcq2tn7mq0"></div>
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-900" data-mp="7zheae42k9n4">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-yellow/10 flex items-center justify-center text-brand-yellow mb-2 md:mb-3" data-id="element-901" data-mp="lw39tiggpmed">
                <PawPrint className="w-6 h-6 md:w-8 md:h-8" data-id="element-902" data-mp="3qabaq46vk5hj" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-903" data-mp="3lc9qvh1mk250">
                3. Huellas
              </span>
              <span className="hidden md:block text-xs text-gray-500 mt-1" data-id="element-904" data-mp="304vc4gbjmpt">
                1 Like = 1 Huella
              </span>
            </div>
            <div className="hidden md:block w-12 h-0.5 bg-gray-200 mt-7" data-id="element-905" data-mp="36mlr4k93dbje"></div>
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-906" data-mp="32licg12s5ljr">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green mb-2 md:mb-3" data-id="element-907" data-mp="32jpcf4mo9zkq">
                <Trophy className="w-6 h-6 md:w-8 md:h-8" data-id="element-908" data-mp="2x9d4awqa8uj2" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-909" data-mp="2ijxgnpm1t57l">
                4. ¡Gana!
              </span>
              <span className="hidden md:block text-xs text-gray-500 mt-1" data-id="element-910" data-mp="2nge74qgpzvnu">
                Mascota Influencer
              </span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div ref={topCtaRef} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="mt-10" data-id="element-911" data-mp="3g536ajy7tqwy">
            <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-3 bg-brand-cyan hover:bg-blue-700 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-full font-bold text-base md:text-lg transition-all shadow-lg hover:shadow-cyan-500/30 transform hover:-translate-y-1" data-id="element-912" data-mp="1c5y5nzqiy74q">
              <ClipboardEdit className="w-5 h-5 md:w-6 md:h-6" data-id="element-913" data-mp="17dn30p4eg78h" />
              Registra a tu mascota
            </button>
          </motion.div>
        </div>

        {/* NEW: Info banner */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-10 mx-auto max-w-3xl bg-brand-green/10 border border-brand-green/30 rounded-2xl p-5 flex items-start gap-3" data-id="element-914" data-mp="yfb29u9tjt2b">
          <Info className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-915" data-mp="2irqwlu72rtd0" />
          <p className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-916" data-mp="77avw5z1k1u8">
            <strong className="text-brand-green" data-id="element-917" data-mp="28pwg8rm5m6u">
              ¿Ya te inscribiste a la Caminata Canina?
            </strong>{' '}
            Tus mascotas aparecen aquí automáticamente 🎉. Si aún no lo has
            hecho,{' '}
            <Link to="/caminata-canina" className="text-brand-green font-bold underline hover:text-brand-navy transition-colors" data-id="element-918" data-mp="c4jfmazu8dxl">
              inscríbete a la Caminata
            </Link>{' '}
            para sumar a tu peludo al Muro.
          </p>
        </motion.div>

        {/* Mobile-only podium Top 5 */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="lg:hidden mb-6 bg-brand-navy rounded-2xl p-4 shadow-xl" data-id="element-919" data-mp="uw2jsl5lly17">
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-920" data-mp="15j5dqpuyb246">
            <Trophy className="w-5 h-5 text-brand-yellow" data-id="element-921" data-mp="15li4nsiosmtd" />
            <h3 className="text-base font-bold text-white" data-id="element-922" data-mp="19kh9ld4f4jkt">
              Top 5 Influencers
            </h3>
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1" data-id="element-923" data-mp="1ei8xlw1xzljq">
            {topPets.map((pet, index) => <div key={pet.id} className="flex-shrink-0 flex flex-col items-center w-16" data-id="element-924" data-mp="1oi9mrymhsvvu" data-mpi={index}>
                <div className="relative" data-id="element-925" data-mp="1taj5atla2to3">
                  <div className={`w-14 h-14 rounded-full bg-gray-100 border-2 ${index === 0 ? 'border-brand-yellow' : 'border-white/20'} overflow-hidden`} data-id="element-926" data-mp="1tblzbadwdc88">
                    {pet.imageUrl && <img src={pet.imageUrl} alt={pet.name} className="w-full h-full object-cover" data-id="element-927" data-mp="2787ozy3lzurk" />}
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-brand-yellow text-brand-navy text-[10px] font-bold flex items-center justify-center shadow" data-id="element-928" data-mp="2ccjr397kev8u">
                    {index + 1}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-white mt-1.5 truncate w-full text-center" data-id="element-929" data-mp="303p31dadx3dv">
                  {pet.name}
                </span>
                <span className="text-[10px] text-brand-cyan font-bold" data-id="element-930" data-mp="2v6oxd3hg62na">
                  {pet.huellas}
                </span>
              </div>)}
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-10" data-id="element-931" data-mp="2ma7vyryvxj4q">
          {/* Main Grid (Instagram style) */}
          <div className="w-full lg:w-2/3" data-id="element-932" data-mp="3ivrl8t2ocrq8">
            {loading ? <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" data-id="element-933" data-mp="3e1x7e69r36z2">
                {Array.from({
              length: 6
            }).map((_, i) => <div key={i} className="bg-white/60 rounded-2xl border border-gray-200 overflow-hidden animate-pulse" data-id="element-934" data-mp="33lbkkc15tvp9" data-mpi={i}>
                    <div className="w-full aspect-square bg-gray-200" data-id="element-935" data-mp="2tlc8441mdl74" />
                    <div className="p-2.5 md:p-4 space-y-2" data-id="element-936" data-mp="3hboom3axkq9b">
                      <div className="h-3 bg-gray-200 rounded w-2/3" data-id="element-937" data-mp="3hc87bm8enioe" />
                      <div className="h-7 bg-gray-200 rounded" data-id="element-938" data-mp="3mb1dsup9y9f2" />
                    </div>
                  </div>)}
              </div> : pets.length === 0 ? <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200 p-10 text-center" data-id="element-939" data-mp="3ckgcgzi7uqsg">
                <div className="w-16 h-16 rounded-full bg-brand-cyan/10 flex items-center justify-center mx-auto mb-4" data-id="element-940" data-mp="443qw4jc2les">
                  <PawPrint className="w-8 h-8 text-brand-cyan" data-id="element-941" data-mp="8xg1har6groj" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-942" data-mp="1tkzanxnzsx5g">
                  Aún no hay mascotas en el Muro
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-943" data-mp="1olcw9kwfhav2">
                  Sé el primero en sumar a tu peludo. Inscríbete a la Caminata
                  Canina y tu mascota aparecerá aquí automáticamente.
                </p>
                <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold transition-all shadow-lg" data-id="element-944" data-mp="1ammptenpxyu1">
                  <ClipboardEdit className="w-5 h-5" data-id="element-945" data-mp="1kj993z0echty" /> Registra a tu mascota
                </button>
              </div> : <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" data-id="element-946" data-mp="2vzg0wbedp3ea">
                  {pets.slice(0, HOME_LIMIT).map((pet, index) => <PetCard key={pet.id} pet={pet} index={index} hasVoted={votedIds.has(pet.id)} onVote={handleDarHuella} data-id="element-947" data-mp="2y4w7map43fh6" data-mpi={index} />)}
                </div>
                {pets.length > HOME_LIMIT && <div className="mt-8 text-center" data-id="element-948" data-mp="3d13mqwlnxqpl">
                    <Link to="/muro" className="inline-flex items-center gap-2 bg-brand-navy hover:bg-[#1a2a8a] text-white px-8 py-3.5 rounded-full font-bold text-base md:text-lg transition-all shadow-lg hover:-translate-y-0.5" data-id="element-949" data-mp="2feei0ezkt001">
                      Ver todas las mascotas
                      <ArrowRight className="w-5 h-5" data-id="element-950" data-mp="2u4c8x2ow43sx" />
                    </Link>
                  </div>}
              </>}
          </div>

          {/* Sidebar Ranking (desktop only) */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="w-full lg:w-1/3 hidden lg:block" data-id="element-951" data-mp="21lf24bvvt3ry">
            <div className="bg-brand-navy rounded-3xl p-6 shadow-xl sticky top-28" data-id="element-952" data-mp="4rknb2eax6bp">
              <div className="flex gap-3 mb-6 border-b border-white/10 pb-4 items-center justify-center" data-id="element-953" data-mp="3vzf84z46f09k">
                <Trophy className="w-8 h-8 text-brand-yellow" data-id="element-954" data-mp="3r6vmdevc4yto" />
                <h3 className="text-2xl font-bold text-white" data-id="element-955" data-mp="3n5k4jvipdhw1">
                  Top 5 Influencers
                </h3>
              </div>

              <div className="space-y-4 mb-8" data-id="element-956" data-mp="3i8jyhklusf4x">
                {topPets.map((pet, index) => <div key={pet.id} className="flex items-center gap-4 bg-white/5 rounded-xl p-3 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-957" data-mp="3d6kwof5rhqa3" data-mpi={index}>
                    <div className="w-8 text-center font-bold text-brand-yellow text-xl" data-id="element-958" data-mp="10ydvkdyahjxs">
                      #{index + 1}
                    </div>
                    <div className={`w-12 h-12 rounded-full bg-gray-100 border-2 border-white/20 flex-shrink-0 overflow-hidden`} data-id="element-959" data-mp="qzy9ipe4d0v5">
                      {pet.imageUrl && <img src={pet.imageUrl} alt={pet.name} className="w-full h-full object-cover" data-id="element-960" data-mp="h8kaeiq2y32j" />}
                    </div>
                    <div className="flex-grow" data-id="element-961" data-mp="d81i6nk9yuzt">
                      <h4 className="font-bold text-white text-sm" data-id="element-962" data-mp="d8tddx9ymqpk">
                        {pet.name}
                      </h4>
                      <p className="text-xs text-white/60" data-id="element-963" data-mp="3ui89fxoa5nh3">{pet.breed}</p>
                    </div>
                    <div className="text-right" data-id="element-964" data-mp="3byfngligucc">
                      <span className="font-bold text-brand-cyan text-sm" data-id="element-965" data-mp="3fsogxfwy8h5o">
                        {pet.huellas}
                      </span>
                      <p className="text-[10px] text-white/50 uppercase" data-id="element-966" data-mp="3btnyvzmapijq">
                        Huellas
                      </p>
                    </div>
                  </div>)}
              </div>

              <button onClick={() => setModalOpen(true)} className="w-full block text-center bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-xl font-bold text-lg transition-colors shadow-lg" data-id="element-967" data-mp="3kuvx6hotajdt">
                Registra a tu mascota
              </button>
            </div>
          </motion.div>
        </div>

        {/* Green Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-16 bg-brand-green/10 border border-brand-green/20 rounded-2xl p-6 text-center max-w-3xl mx-auto" data-id="element-968" data-mp="7fptfgzp5ytn">
          <p className="text-brand-green flex items-center justify-center gap-2 text-base md:text-2xl font-bold leading-snug" data-id="element-969" data-mp="12u7g3pzn45u5">
            <Heart className="w-5 h-5 md:w-6 md:h-6 fill-brand-green flex-shrink-0" data-id="element-970" data-mp="yuf1avjnl3gc" />
            <span data-id="element-971" data-mp="b4kqskmt3qz9">
              Parte de cada inscripción apoya al CEIBA Rionegro, nuestra
              fundación aliada 2026.
            </span>
          </p>
        </motion.div>
      </div>

      {/* Floating pill CTA (mobile only, when section is in view AND top CTA is out of view).
                  Anchored bottom-left with right-side padding to leave room for the WhatsApp FAB. */}
      <AnimatePresence data-id="element-972" data-mp="3nk553n530iuc">
        {showStickyBottomCta && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="md:hidden fixed bottom-6 left-4 right-24 z-40 pointer-events-none" data-id="element-973" data-mp="3jk4el44pkh6x">
            <button onClick={() => setModalOpen(true)} className="pointer-events-auto w-full inline-flex items-center justify-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-5 py-3.5 rounded-full font-bold text-sm transition-all shadow-[0_8px_24px_rgba(0,188,212,0.45)]" data-id="element-974" data-mp="3s6fje8go2cwt">
              <ClipboardEdit className="w-5 h-5" data-id="element-975" data-mp="3n6t31q5jkgjs" />
              Registra a tu mascota
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}